import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HttpStatusCode, isAxiosError } from 'axios';
import { ALERT_RULES } from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useInvalidateAlertsRelatedData from 'shared/hooks/useInvalidateAlertsRelatedData';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from '../services/api';
import { FetchAlertRules } from '../types';

const useReorderAlertRules = () => {
  const selectedEnvId = useSelectedEnv();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const { invalidateAlertsDataQueries } = useInvalidateAlertsRelatedData();
  const alertRulesQueryKey = [ALERT_RULES, selectedEnvId!];

  const { mutate, isLoading } = useMutation(
    (orderedRuleIds: string[]) =>
      api.updateAlertRulesOrder(selectedEnvId!, orderedRuleIds),
    {
      onMutate: async (orderedRuleIds) => {
        await queryClient.cancelQueries(alertRulesQueryKey);
        const previousAlertRules =
          queryClient.getQueryData<FetchAlertRules>(alertRulesQueryKey);
        const positionById = new Map(
          orderedRuleIds.map((id, position) => [id, position]),
        );
        const positionOf = (id: string) =>
          positionById.get(id) ?? orderedRuleIds.length;
        queryClient.setQueryData<FetchAlertRules>(
          alertRulesQueryKey,
          (current) =>
            current && {
              ...current,
              routing_rules: [...current.routing_rules].sort(
                (a, b) => positionOf(a.id) - positionOf(b.id),
              ),
            },
        );
        return { previousAlertRules };
      },
      onError: (error, _, context) => {
        if (context?.previousAlertRules) {
          queryClient.setQueryData(
            alertRulesQueryKey,
            context.previousAlertRules,
          );
        }
        const isConflict =
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.Conflict;
        openSnackbar({
          message: isConflict
            ? 'Alert rules changed, the list was refreshed. Please try again.'
            : 'Something went wrong. Alert rules were not reordered.',
          variant: 'error',
        });
      },
      onSettled: () => invalidateAlertsDataQueries(),
    },
  );

  return { reorderAlertRules: mutate, isReorderInFlight: isLoading };
};

export default useReorderAlertRules;
